import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from "react";
import { AuthUser, AuthError } from "./types";
import authService from "./authService";
import { useIdleTimeout } from "./useIdleTimeout";
import { touchIdleClock, clearIdleClock } from "./idleClock";
import { SessionTimeoutWarning } from "./SessionTimeoutWarning";
import { signInApi, signOutApi, refreshSessionApi } from "utils/authApi";
import { setSessionExpiry, resetSession } from "utils/appSession";
import { queryClient } from "utils/queryClient";
import { useDispatch } from "react-redux";
import { resetUserSession, setAuthUid } from "store/userSlice";
import { store } from "store/store";
import LoadingModal from "modals/loading/LoadingModal";
import { apiErrorMessage } from "utils/errors/parseApiError";

interface AuthState {
  user: AuthUser | null;
  initializing: boolean;
  loading: boolean;
  signingOut: boolean;
  error: AuthError | null;
}

interface AuthContextValue extends AuthState {
  signIn: (identifier: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const dispatch = useDispatch();
  const [state, setState] = useState<AuthState>({
    user: null,
    initializing: true,
    loading: false,
    signingOut: false,
    error: null,
  });

  useEffect(() => {
    let didInit = false;
    const unsubscribe = authService.onAuthStateChanged((user) => {
      if (didInit && !user) {
        window.location.replace("/");
      }
      // NOTE: this listener deliberately does NOT write authUid. It fires in every
      // tab whenever the origin-shared Firebase user changes, including when
      // ANOTHER tab signs a different account in. Recording the uid here would
      // make this tab silently adopt that account — the exact bug (CRYP-4918).
      // Only signIn() below, i.e. a sign-in performed in THIS tab, may bind it;
      // the resulting mismatch is what the route guards detect.

      const storedAuthUid = store.getState().user.authUid;
      const bound = !!user && storedAuthUid === user.uid;

      if (!bound) {
        // Don't probe: /auth/refresh mints a fresh hour, and this tab is not
        // going to display this account.
        setState((prev) => ({ ...prev, user, initializing: false }));
        didInit = true;
        return;
      }

      // Confirm the session is alive before anything renders, or the guards
      // authorize from localStorage and paint the dashboard over a dead cookie.
      refreshSessionApi()
        .then(({ sessionExpiresAt }) => {
          setSessionExpiry(sessionExpiresAt);
          setState((prev) => ({ ...prev, user, initializing: false }));
        })
        .catch(async (error: any) => {
          if (error?.response?.status === 401) {
            resetSession();
            clearIdleClock();
            queryClient.clear();
            await authService.signOut();
            setState((prev) => ({ ...prev, user: null, initializing: false }));
            return;
          }
          // No answer is not the same as "not authenticated" — don't destroy a
          // possibly-valid session over a flaky connection.
          setState((prev) => ({ ...prev, user, initializing: false }));
        });
      didInit = true;
    });
    return unsubscribe;
  }, []);

  const signIn = async (identifier: string, password: string) => {
    // Start from a clean slate. The sign-out paths clear this too, but they are not the only way
    // a session ends: a 401 signs out from the axios interceptor and reloads the page immediately,
    // and redux-persist's write is batched, so it can lose that race. Clearing here is the one
    // point every sign-in passes through, whatever ended the last one.
    dispatch(resetUserSession());
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const { customToken, sessionExpiresAt, tenantId } = await signInApi({
        identifier,
        password,
      });
      setSessionExpiry(sessionExpiresAt);
      touchIdleClock();
      const { user } = await authService.signInWithCustomToken(
        customToken,
        tenantId,
      );
      // Bind this tab's stored identity to the uid it just authenticated as. This
      // is the ONLY place authUid is written — a sign-in performed in this tab.
      //
      // It must happen here rather than in the login form, and in the same
      // synchronous block as the setState below: the guards read authUid on every
      // render, so any render between "user is set" and "authUid is set" would see
      // a mismatch and replace the login form with the session-changed screen
      // mid-sign-in. Keeping them together makes that window impossible.
      dispatch(setAuthUid(user?.uid));
      setState((prev) => ({ ...prev, user, loading: false }));
    } catch (err: any) {
      setState((prev) => ({
        ...prev,
        loading: false,
        error: {
          code: err.code ?? "auth/unknown",
          message: apiErrorMessage(err, "Sign in failed"),
        },
      }));
      throw err;
    }
  };

  const signOut = useCallback(async () => {
    setState((prev) => ({ ...prev, signingOut: true, error: null }));
    try {
      await signOutApi();
    } catch {
      // best effort — always clear local state
    }
    try {
      resetSession();
      clearIdleClock();
      // Drop every cached query so the next sign-in can't read the previous
      // session's data. The auth-state listener above also reloads the page on
      // sign-out, which would clear it anyway — this makes the teardown
      // explicit instead of a side effect of that redirect.
      queryClient.clear();
      // The redux half of the same teardown. It is persisted, so without this the treasury id
      // and userType outlive the session — and a stale userType makes GuestOnlyRoute bounce the
      // NEXT sign-in past its login form before that flow can finish.
      dispatch(resetUserSession());
      await authService.signOut();
      setState((prev) => ({ ...prev, user: null, signingOut: false }));
    } catch (err: any) {
      setState((prev) => ({
        ...prev,
        signingOut: false,
        error: {
          code: err.code ?? "auth/unknown",
          message: apiErrorMessage(err, "Sign out failed"),
        },
      }));
    }
  }, [dispatch]);

  const handleIdleTimeout = useCallback(async () => {
    setState((prev) => ({ ...prev, signingOut: true }));
    try {
      await signOutApi();
    } catch {}
    resetSession();
    clearIdleClock();
    queryClient.clear();
    dispatch(resetUserSession());
    await authService.signOut();
    window.location.replace("/");
  }, [dispatch]);

  const { showWarning, resetTimer } = useIdleTimeout(
    handleIdleTimeout,
    !!state.user,
  );

  const clearError = () => {
    setState((prev) => ({ ...prev, error: null }));
  };

  return (
    <AuthContext.Provider value={{ ...state, signIn, signOut, clearError }}>
      {children}
      <SessionTimeoutWarning
        open={showWarning}
        onStay={resetTimer}
        onSignOut={handleIdleTimeout}
      />
      <LoadingModal
        isOpen={state.initializing}
        loadingMsg="Restoring your session…"
      />
      <LoadingModal isOpen={state.signingOut} loadingMsg="Signing out…" />
    </AuthContext.Provider>
  );
}

export function useAuthContext(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx)
    throw new Error("useAuthContext must be used inside <AuthProvider>");
  return ctx;
}

export default AuthContext;
